import type { Node } from '@tiptap/pm/model';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import lodash from 'lodash';

import type { PmConverters } from './../pm-converters/collect';
import { collectPmConverters } from './../pm-converters/collect';
import type { RicosEditorProps } from './../ricos-common';
import { commonPluginConfig, commonPlugins } from './../ricos-common-plugins';
import type { IEditorQuery } from './../ricos-content-query';
import { editorQueryCreator } from './../ricos-content-query';
import type { DevService, GeneralContext } from './../ricos-context';
import { isEditorPlugin, isEditorPluginOptions } from './../ricos-editor/utils/is-editor-plugin';
import { RicosEvents } from './../ricos-events';
import type { ModalService } from './../ricos-modals';
import { EditorPlugins, PluginsEvents } from './../ricos-plugins';
import { EditorKeyboardShortcuts } from './../ricos-shortcuts';
import { RicosStyles, THEME_DEFAULTS, ZIndexService } from './../ricos-styles';
import type {
  DebugMode,
  EditorPlugin,
  INotifier,
  LegacyEditorPluginConfig,
  Platform,
  PluginExports,
  RicosPortal,
  RicosServices,
  TFunction,
} from './../ricos-types';
import type { UpdateService } from './../_shared/upload/update-service';
import { createUpdateService } from './../_shared/upload/update-service';
import type { IUploadService } from './../_shared/upload/upload-service';
import { UploadService } from './../_shared/upload/upload-service';
import { getLangDir, isSSR } from './../wix-rich-content-common';
import { Content, RicosToolbars } from './../toolbars-v3';

import { getHelpersConfig } from './helpers-config';
import { mapBiCallbacksToSubscriptions } from './map-bi-callbacks-to-events';
import type { RicosEventSource, RicosEventSubscriber } from './orchestrate-events';
import { registerEventSources, registerEventSubscribers } from './orchestrate-events';
import { RicosEditor } from './ricos-editor';
import { RicosSchema } from './ricos-schema';

export class RicosOrchestrator {
  private readonly editorProps: RicosEditorProps & { debugMode?: DebugMode[] };

  private updateService!: UpdateService;

  private readonly events: RicosEvents;

  private readonly toolbars: RicosToolbars;

  private readonly pluginsEvents: PluginsEvents;

  private readonly modals: ModalService;

  private readonly styles: RicosStyles;

  private readonly shortcuts: EditorKeyboardShortcuts;

  private readonly plugins: EditorPlugins;

  private readonly uploadService: IUploadService;

  private readonly t: TFunction;

  private readonly content: Content<Node[]>;

  private readonly editor: RicosEditor;

  private readonly editorQuery: IEditorQuery;

  private readonly context: Omit<GeneralContext, 'theme'>;

  private readonly schema: RicosSchema;

  private readonly zIndexService: ZIndexService;

  private readonly pluginExports: Partial<PluginExports>;

  private readonly converters: PmConverters;

  private readonly devService: DevService;

  constructor({
    editorProps,
    t,
    platform,
    modalService,
    devService,
  }: {
    editorProps: RicosEditorProps & { debugMode?: DebugMode[]; jsdomMode?: boolean };
    t: TFunction;
    platform: Platform;
    modalService: ModalService;
    devService: DevService;
  }) {
    this.t = t;
    this.editorProps = editorProps;

    this.devService = devService;

    this.styles = new RicosStyles();

    const getEditorQuery = (): IEditorQuery => {
      return this.editorQuery;
    };

    this.converters = collectPmConverters(collectAllPlugins(this.editorProps));

    this.content = Content.create<Node[]>([], {
      styles: this.styles,
      converters: this.converters,
      getEditorQuery,
      editor: null,
      modals: modalService,
    });

    const isMobile = editorProps.isMobile || false;

    this.context = {
      t,
      debugMode: editorProps.debugMode || [],
      isMobile,
      languageDir: getLangDir(editorProps.locale),
      locale: editorProps.locale || 'en',
      localeContent: editorProps.localeContent || editorProps.locale || 'en',
      experiments: editorProps.experiments || {},
      platform: platform || 'macOs',
      content: this.content,
      jsdomMode: editorProps.jsdomMode ?? false,
      environment: editorProps.environment || 'liveSite',
      tabIndex: -1,
      portal: null,
    };

    this.updateService = createUpdateService();
    this.events = new RicosEvents(
      (editorProps.debugMode?.includes('events') || editorProps.debugMode?.includes('all')) ?? false
    );

    this.uploadService = new UploadService(this.updateService);

    this.modals = modalService;

    this.shortcuts = new EditorKeyboardShortcuts(this.modals, {
      isDebugMode:
        (editorProps.debugMode?.includes('shortcuts') || editorProps.debugMode?.includes('all')) ??
        false,
    });

    this.pluginsEvents = new PluginsEvents();

    this.schema = new RicosSchema(editorProps.content, this.converters);

    this.toolbars = new RicosToolbars(this.context.isMobile);
    this.toolbars.getShortcuts().map(shortcut => this.shortcuts.register(shortcut));

    this.plugins = new EditorPlugins(
      {
        modals: this.modals,
        uploadService: this.uploadService,
        updateService: this.updateService,
        shortcuts: this.shortcuts,
        t: this.t,
        content: this.content,
        styles: this.styles,
        toolbars: this.toolbars,
        pluginsEvents: this.pluginsEvents,
        context: this.context,
        schema: this.schema,
        converters: this.converters,
        devService,
      },
      this.editorProps.toolbarSettings || {}
    );

    this.registerAllPlugins(commonPluginConfig);

    if (editorProps.keyboardShortcuts) {
      editorProps.keyboardShortcuts.map(shortcut =>
        this.shortcuts.mergeKeyboadShortcutProp(shortcut)
      );
    }

    this.editor = new RicosEditor(
      this.editorProps,
      {
        styles: this.styles,
        plugins: this.plugins,
        updateService: this.updateService,
        uploadService: this.uploadService,
        t: this.t,
        content: this.content,
        shortcuts: this.shortcuts,
        modals: this.modals,
        toolbars: this.toolbars,
        pluginsEvents: this.pluginsEvents,
        context: this.context,
        schema: this.schema,
        getEditorQuery,
        converters: this.converters,
        devService,
      },
      this.editorProps.debugMode?.includes('prosemirror') || editorProps.debugMode?.includes('all')
    );

    this.plugins.setPublicCommands(this.editor.getPublicCommands());
    this.schema.setEditor(this.editor);
    this.content.update([], this.editor.adapter.tiptapEditor);

    this.updateService.setEditorCommands(this.editor.getPublicCommands());

    this.editorQuery = editorQueryCreator(
      this.editor.adapter.tiptapEditor,
      this.styles,
      this.converters
    );

    this.pluginExports = this.plugins.getExports();

    this.zIndexService = new ZIndexService(
      !!editorProps.isMobile,
      editorProps.theme?.settingsStyles
    );

    if (!isSSR()) {
      //@ts-ignore
      window.editorQuery = this.editorQuery;
      //@ts-ignore
      window.ricosOrchestrator = this;
    }

    // NOTE: this should run after entities initializations!
    this.orchestrateEvents();

    this.toolbars.initToolbars(this.editorProps.toolbarSettings, {
      plugins: this.plugins,
      editor: this.editor,
      modals: this.modals,
    });
  }

  // NOTE: it is crucual to keep the event registration/subsciption order to ensure their correct workflow
  private orchestrateEvents() {
    registerEventSources(this.events, [
      this.shortcuts,
      this.modals,
      this.editor as unknown as RicosEventSource,
      this.toolbars,
      this.uploadService,
      this.pluginsEvents,
      this.styles,
    ] as RicosEventSource[]);
    registerEventSubscribers(this.events, [this.modals as unknown as RicosEventSubscriber]);
    registerEventSubscribers(this.events, [
      this.modals as unknown as RicosEventSubscriber,
      this.editor as unknown as RicosEventSubscriber,
      this.styles as unknown as RicosEventSubscriber,
      this.pluginsEvents as unknown as RicosEventSubscriber,
    ]);
    mapBiCallbacksToSubscriptions(this.editorProps, this.events, this.schema);
  }

  setUpdateServiceDom(
    setErrorNotifier: () => INotifier,
    setFileInput: () => HTMLInputElement
  ): void {
    this.uploadService.setErrorNotifier(setErrorNotifier);
    this.uploadService.setHiddenInputElement(setFileInput);
  }

  getServices(): RicosServices {
    return {
      styles: this.styles,
      plugins: this.plugins,
      uploadService: this.uploadService,
      updateService: this.updateService,
      t: this.t,
      shortcuts: this.shortcuts,
      content: this.content,
      modals: this.modals,
      editor: this.editor,
      toolbars: this.toolbars,
      pluginsEvents: this.pluginsEvents,
      context: this.context,
      schema: this.schema,
      zIndexService: this.zIndexService,
      getEditorQuery: () => this.editorQuery,
      converters: this.converters,
      devService: this.devService,
    };
  }

  getPluginExports(): Partial<PluginExports> {
    return this.pluginExports;
  }

  private registerAllPlugins(commonPluginConfig: LegacyEditorPluginConfig) {
    const helpersConfig = getHelpersConfig(this.editorProps);

    pipe(
      collectAllPlugins(this.editorProps),
      EditorPlugins.mergeConfig(this.editorProps._rcProps?.config),
      EditorPlugins.mergeConfig(commonPluginConfig),
      EditorPlugins.mergeConfig(helpersConfig),
      A.map((plugin: EditorPlugin) => this.plugins.register(plugin))
    );
  }

  setPortalRef(portal: RicosPortal) {
    this.context.portal = portal;
  }
}

const collectAllPlugins = (editorProps: RicosEditorProps) => {
  const { plugins } = editorProps;
  const collectPluginsWithDependencies = (plugins: EditorPlugin[]): EditorPlugin[] =>
    plugins.flatMap(plugin => [
      plugin,
      ...(plugin.dependencies ? collectPluginsWithDependencies(plugin.dependencies) : []),
    ]);

  const editorPlugins = plugins?.filter(isEditorPlugin) || [];
  const editorPluginOptions = plugins?.filter(isEditorPluginOptions) || [];

  const collectedPlugins = collectPluginsWithDependencies([
    ...commonPlugins(editorProps, editorPluginOptions),
    ...editorPlugins,
  ]);
  return lodash.uniq(collectedPlugins);
};
